import React, { useContext, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { App, Modal, Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { postCoreEvent } from '../../../../services/core-event'
import UserLeaveTypeForm from '@vacationtracker/shared/components/user-leave-type-form'
import getValidityStart from '@vacationtracker/shared/functions/get-validity-start'
import getvalidityStartForQuota from '@vacationtracker/shared/functions/get-validity-start-for-senioirty-enitlement'
import { getAverageWorkingHours, getUserWorkingHoursDependingOnType } from '@vacationtracker/shared/functions/work-week'
import { IUserLeaveTypesUpdateEvent } from '@vacationtracker/shared/types/leave-type'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'

import * as logger from '../../../../services/logger'
import { showApiErrorNotification } from '../showApiErrorNotification'
import IntlMessages from '../../../../util/IntlMessages'
import { graphqlRequest } from '../../../../services/tanstack-query'
import { getUserForQuotaEdit } from '../../../../graphql/custom-queries'
import { notificationStore } from '../../../../context/notificationsContext/store'
import { IUserQuota } from '../../../../types/custom-queries'

interface IGetUserForQuotaEdit {
  getUser: {
    id: string
    name: string
    startDate?: string
    location?: { id: string; resetQuotas?: string }
    leaveDays: IUserQuota[]
  }
}

export interface IQuotaEditDialogProps {
  userId: string
  leaveTypeId: string
  hourlyLeaveAccounting: boolean
  dateFormat: string
  locale?: LocaleEnum
  onClose: () => void
  onSaved: (userId: string) => void
}

export const QuotaEditDialog = (props: IQuotaEditDialogProps): React.ReactElement => {
  const { userId, leaveTypeId, hourlyLeaveAccounting, dateFormat, locale, onClose, onSaved } = props
  const { formatMessage } = useIntl()
  const { notification } = App.useApp()
  const { addActionNotifications } = useContext(notificationStore)

  const [data, setData] = useState<IGetUserForQuotaEdit['getUser'] | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    graphqlRequest<IGetUserForQuotaEdit>(getUserForQuotaEdit, { id: userId })
      .then((response) => {
        if (!cancelled) {
          setData(response.getUser)
        }
      })
      .catch((error) => {
        logger.warning(error)
        if (!cancelled) {
          setFailed(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [userId])

  useEffect(() => {
    if (failed) {
      notification.error({ message: formatMessage({ id: 'error.generic' }) })
      onClose()
    }
  }, [failed])

  const now = new Date()
  const currentPeriod = data?.leaveDays?.find((period) => new Date(period.yearStart) <= now && now <= new Date(period.yearEnd))
  const leaveType = currentPeriod?.leaveTypes?.find((entry) => entry.leaveTypeId === leaveTypeId)
  const loadedWithoutLeaveType = Boolean(data && currentPeriod && !leaveType)

  useEffect(() => {
    if (loadedWithoutLeaveType) {
      onClose()
    }
  }, [loadedWithoutLeaveType])

  if (!data || !currentPeriod || !leaveType) {
    if (loadedWithoutLeaveType) {
      return <></>
    }
    return (
      <Modal open footer={null} closable={false}>
        <Spin />
      </Modal>
    )
  }

  const isInitialPeriod = data.leaveDays.length > 0 && data.leaveDays[0].yearStart === currentPeriod.yearStart && data.leaveDays[0].yearEnd === currentPeriod.yearEnd

  const handleSave = async (formData: IUserLeaveTypesUpdateEvent) => {
    const validityStart = getValidityStart(currentPeriod, currentPeriod)
    const validityStartForQuota = getvalidityStartForQuota(currentPeriod, currentPeriod)
    const workingHours = getAverageWorkingHours(getUserWorkingHoursDependingOnType(currentPeriod.userWorkingHours) || [])

    const proratedLeaveTypes: string[] = formData.shouldProrate
      ? [...new Set([...(leaveType.userProratedLeaveTypes || []), formData.leaveTypeId])]
      : (leaveType.userProratedLeaveTypes || []).filter((entry) => entry !== formData.leaveTypeId)

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updates: any = {
      eventType: 'USER_LEAVE_TYPES_UPDATED',
      eventGroup: 'USER',
      userId,
      leaveTypeId: formData.leaveTypeId,
      daysPerYear: formData.quotaInUnits,
      ...(formData.seniorityEntitlementForYear !== undefined && {
        seniorityEntitlementDaysForYear: formData.unit === 'hours' ? formData.seniorityEntitlementForYear / workingHours : formData.seniorityEntitlementForYear,
        seniorityEntitlementHoursForYear: formData.unit === 'hours' ? formData.seniorityEntitlementForYear : formData.seniorityEntitlementForYear * workingHours,
      }),
      ...(formData.entitlementByRoleForYear !== undefined && {
        entitlementByRoleDaysForYear: formData.unit === 'hours' ? formData.entitlementByRoleForYear / workingHours : formData.entitlementByRoleForYear,
        entitlementByRoleHoursForYear: formData.unit === 'hours' ? formData.entitlementByRoleForYear : formData.entitlementByRoleForYear * workingHours,
      }),
      hasUnlimitedDays: Boolean(formData.hasUnlimitedDays),
      currentYearOnly: !formData.applyToFutureYears,
      ignoreRolloverFromPreviousYear: formData.ignoreRolloverFromPreviousYear,
      shouldProrate: isInitialPeriod ? proratedLeaveTypes.length > 0 : undefined,
      proratedLeaveTypes: isInitialPeriod ? proratedLeaveTypes : undefined,
      validityStart,
      validityStartForQuota,
      quotaSetIn: formData.unit,
      hoursPerYear: 0,
    }

    if (hourlyLeaveAccounting) {
      updates.daysPerYear = formData.quotaInUnits / workingHours
      updates.hoursPerYear = formData.quotaInUnits
    }
    if (formData.hasUnlimitedDays) {
      updates.daysPerYear = 0
      updates.hoursPerYear = 0
      updates.quotaSetIn = hourlyLeaveAccounting ? 'hours' : 'days'
    }

    try {
      const response = await postCoreEvent(updates, 'users-table')
      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: 'users.updateQuota.InProgress' }),
        icon: <LoadingOutlined />,
        duration: 0,
      })
      addActionNotifications([response.correlationId])
      onSaved(userId)
    } catch (error) {
      logger.warning({ errorType: 'ERROR UPDATE QUOTA', error })
      showApiErrorNotification(notification, formatMessage, error)
    }
    onClose()
  }

  return (
    <UserLeaveTypeForm
      onSave={handleSave}
      handleCancel={onClose}
      visible={true}
      leaveType={leaveType}
      IntlMessages={IntlMessages}
      dateRange={{
        name: `${dayjs(currentPeriod.yearStart).format(dateFormat)} - ${dayjs(currentPeriod.yearEnd).format(dateFormat)}`,
      }}
      hourlyLeaveAccounting={hourlyLeaveAccounting}
      userStartDate={data.startDate}
      locationResetQuotas={data.location?.resetQuotas}
      userName={data.name}
      workingHours={getUserWorkingHoursDependingOnType(currentPeriod.userWorkingHours) || []}
      period={{
        start: currentPeriod.yearStart as string,
        end: currentPeriod.yearEnd as string,
      }}
      locale={locale}
      isInitialPeriod={isInitialPeriod}
    />
  )
}
